import "../../../assets/scss/instance-card-style.scss";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { chooseInstance } from "../../features/instance/instanceSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { InstanceCardInterface } from "../../utility/interface/instance-card";

import { RightOutlined } from "@ant-design/icons";
import { getDockerFileList } from "../../server/GoogleDrive";
import {
  updateCurrDockerFolderName,
  updateDockerList,
  updatedSelectedDocker,
  updateFolderPath,
} from "../../features/cloud/cloudSlice";

import { InstanceDetailModal } from "../InstanceDetailModal";
import { useElapsedTime } from "../../hooks/useElapsedTime";
import { FileIcon } from "../../icons/fileIcon";
import { Modal } from "antd";
import DockerFileList from "../DockerFileList";

export const InstanceCard = (props: InstanceCardInterface) => {
  const { t } = useTranslation();
  const cloudState = useAppSelector((state: any) => state.cloud);
  const settingsState = useAppSelector((state: any) => state.settings);
  const userState = useAppSelector((state: any) => state.user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileListLoading, setFileListLoading] = useState(false);
  const [fileListError, setFileListError] = useState(false);
  const instanceLoadingStatusState = useAppSelector(
    (state: any) => state.instanceStatus.id,
  );
  const {
    idx,
    instance_id,
    host_id,
    time_consumed,
    price,
    gpu_available,
    product_name,
    service_url,
    ssh_url,
    public_key,
    template_name,
    host_name,
    node_name,
    healthy,
    gpu_count,
    isSelected,
    onSelect,
    created_at,
    internal_ip,
    ssh_password,
    notready,
    payment,
    secondaryLoading,
  } = props;
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const { days, hours, minutes, seconds } = useElapsedTime(created_at!);

  const dispatch = useAppDispatch();

  const selectInstance = () => {
    if (notready) return;
    setIsModalOpen(true);

    if (isInstanceActive(instance_id)) {
      return;
    } else {
      getDockerFiles(instance_id, host_id);
      dispatch(
        chooseInstance({
          instance_id: instance_id,
          host_id: host_id,
          price: price,
          gpu_available: gpu_available,
          service_url: service_url,
          ssh_url: ssh_url,
          time_consumed: time_consumed,
          user_id: userState.user_id,
          product_name: product_name,
          node_name: node_name,
          public_key: public_key,
        }),
      );
    }
  };

  const getDockerFiles = async (instance_id: any, host_id: any) => {
    setFileListLoading(true);
    setFileListError(false);
    getDockerFileList({
      command: 1,
      new_file_name: "",
      search_keyword: "",
      file: "",
      instance_id: instance_id,
      host_id: host_id,
      user_id: userState.user_id,
    })
      .then((res) => {
        dispatch(updateDockerList(res.data.ops_result));
        dispatch(
          updatedSelectedDocker({
            user_id: userState.user_id,
            instance_id: instance_id,
            host_id: host_id,
          }),
        );
        setFileListLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setFileListError(true);
        setFileListLoading(false);
      });
  };

  const isInstanceActive = (instance_id: any) => {
    return instanceLoadingStatusState.some(
      (item: any) => String(item.instance_id) === String(instance_id),
    );
  };

  const getStatus = () => {
    const foundInstance = instanceLoadingStatusState.find(
      (item: any) => String(item.instance_id) === String(instance_id),
    );
    if (foundInstance !== undefined) {
      if (foundInstance.message.includes("successfully")) {
        setLoadingMessage("none");
      } else {
        setLoadingMessage(foundInstance.message);
      }
    } else {
      setLoadingMessage("none");
    }
  };

  const returnHealthyStatus = (status: number) => {
    if (status === 0 || status === 2) {
      return "#F00";
    }

    if (status === 1) {
      return "#05D35C";
    }
  };

  const handleFileListModal = (instance_id: any, host_id: any) => {
    if (!notready) {
      getDockerFiles(instance_id, host_id);
      setIsFileModalOpen(true);
    } else {
      return;
    }
  };

  const closeFileModal = () => {
    dispatch(updateCurrDockerFolderName(""));
    dispatch(updateFolderPath(""));
    dispatch(updateDockerList(""));
    setIsFileModalOpen(false);
  };

  useEffect(() => {
    if (typeof instanceLoadingStatusState !== "string") {
      getStatus();
    }
  }, [instanceLoadingStatusState]);

  return (
    <>
      <div
        className={`instance-card ${isSelected ? "selected" : ""}`}
        onClick={onSelect}
      >
        <div className="instance-card-hero">
          <div
            className={`file-icon ${!isSelected ? "disabled" : ""}`}
            onClick={(e) => { e.stopPropagation(); if (isSelected) handleFileListModal(instance_id, host_id); }}
          >
            <FileIcon />
          </div>

          <img src="/images/templates/trellis.png" alt="template" />
          <div className="overlay">
            {loadingMessage !== "none" ? (
              <p>{loadingMessage}</p>
            ) : (
              <p>
                {days !== 0 && `${days} ${t("Days")}`}{" "}
                {hours !== 0 && `${hours} ${t("hours")}`}{" "}
                {minutes !== 0 && `${minutes} ${t("Minutes")}`}{" "}
                {seconds && `${seconds} ${t("Seconds")}`}
              </p>
            )}
          </div>

          <div className="healthy-status-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="12"
                fill={
                  loadingMessage === "none"
                    ? returnHealthyStatus(healthy!)
                    : "#FFC501"
                }
              />
            </svg>
          </div>
        </div>

        <div className="instance-card-body">
          <h3>{template_name}</h3>
          <div className="details-grid">
            <div>
              <span>{t("GPU Type")}</span>
              <strong>{product_name}</strong>
            </div>
            <div>
              <span>{t("GPU Amount")}</span>
              <strong>{gpu_count} GPU</strong>
            </div>
          </div>
          <div className={`view-detail ${!isSelected ? "disabled" : ""}`} onClick={(e) => { e.stopPropagation(); if (isSelected) selectInstance(); }}>
            <span>
              {t("View Detail")} <RightOutlined />
            </span>
          </div>
        </div>
      </div>
      <InstanceDetailModal
        visible={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        instance={props}
      />
      <Modal
        closable={{ "aria-label": "Custom Close Button" }}
        width={"90vw"}
        footer={null}
        open={isFileModalOpen}
        onOk={() => setIsFileModalOpen(false)}
        onCancel={() => closeFileModal()}
        className="docker-file-list-modal"
        style={{ top: 40, paddingBottom: 0 }}
      >
        <div className="docker-file-list-wrapper">
          <DockerFileList
            dockerList={cloudState.dockerList}
            isLoading={fileListLoading}
            isError={fileListError}
          />
        </div>
      </Modal>
    </>
  );
};
